import { useAuth0 } from "@auth0/auth0-react";
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import { JsonFromString } from "io-ts-types";
import Cookie from "js-cookie";
import type { FC } from "react";
import { useEffect, useState } from "react";
import config from "src/config";
import { useHostnameWhenPresent } from "src/utils/hooks/hostname";
import { useAccessToken } from "src/utils/use-access-token";
import * as OE from "../../utils/option-either";
import { fetchMe } from "./fetchers";

export const USER_DOMAIN_COOKIE = "user-domain";
export const userDomainCookie = t.type({
  domain: t.string,
  apiUrl: t.string,
});
export type UserDomainCookie = t.TypeOf<typeof userDomainCookie>;

export const UserDomainCookie =
  /*#__PURE__*/ JsonFromString.pipe(userDomainCookie);

/*
 * Used to allow a cross-subdomain cookie to be set so that authenticated
 * user can be directed to their organisation's URL
 */
export const usePersistUserDomain = (isAuthenticated: boolean) => {
  const accessToken = pipe(useAccessToken(), OE.noneAndErrorToUndefined);
  const hostname = useHostnameWhenPresent();

  useEffect(() => {
    async function setMeCookie() {
      const me = await fetchMe.fetch(accessToken, {});

      if (me.me?.organisation?.subdomain != null) {
        Cookie.set(
          USER_DOMAIN_COOKIE,
          UserDomainCookie.encode({
            domain: me.me.organisation.subdomain,
            apiUrl: config.publicApiUrl,
          }),
          {
            domain: `.${hostname ?? "hortis.com"}`,
            expires: 365,
          },
        );
      }
    }
    if (accessToken != null && isAuthenticated) {
      void setMeCookie();
    }
  }, [accessToken, isAuthenticated, hostname]);
};

export const usePersistedDomain = () => {
  const [domain, setDomain] = useState<UserDomainCookie | undefined>();

  useEffect(() => {
    const cookie = Cookie.get(USER_DOMAIN_COOKIE);
    const domain =
      cookie == null
        ? undefined
        : pipe(
            cookie,
            UserDomainCookie.decode,
            E.getOrElseW(() => undefined),
          );

    setDomain(domain);
  }, [setDomain]);

  return domain;
};

export const PersistUserDomain: FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth0();
  usePersistUserDomain(isAuthenticated);
  return <>{children}</>;
};
